export {HomeSub as Page};

import messages from "../../messages/messages.js";
import API_URL, {withSite} from "../../includes/api.js";
import {useEffect, useState} from "preact/hooks";
import {Helmet} from "react-helmet-async";
import ReactPlayer from "react-player/youtube";
import {ArrowDownIcon, XMarkIcon} from "@heroicons/react/24/solid";
import {RadioComponent} from "../../components/RadioComponent.jsx";
import {usePageContext} from "../../hooks/usePageContext.js";
import {linkify} from "../../utils/linkify.jsx";
import {mainSiteUrl} from "../../../tenant.config.js";
import {pickRandom} from "../../utils/pickRandom.js";

function HomeSub() {

    const pageContext = usePageContext();
    const year = new Date().getFullYear();
    // hostname/subdomain are resolved once in +onBeforeRoute.js (same value
    // server- and client-side) since this component renders during SSR.
    const domainSplit = (pageContext?.hostname || '').split('.');
    const subdomain = pageContext?.subdomain || domainSplit[0] || '';
    // Derived from the request host (alias-aware) so staging stays on erocthgin.com.
    const mainUrl = mainSiteUrl(pageContext?.hostname);

    // Seeded from pages/vanity/+data.shared.js's server fetch so <Helmet>/hero aren't empty on first paint.
    // +data.shared.js already redirected unknown/disabled subscribers server-side; the client fetch
    // below only runs when seeding failed (API unreachable) and then owns that redirect.
    // +data.shared.js already picked the six playlists to show. Nothing random may run
    // here: this body executes on the server and again during hydration, and any
    // difference between the two leaves card titles pointing at the wrong links.
    const initialSubscriber = pageContext?.data?.subscriber ?? null;
    const initialPlaylists = initialSubscriber?.playlists ?? [];

    const [videos, setVideos] = useState(initialSubscriber?.videos ?? []);
    const [playlists, setPlaylists] = useState(initialPlaylists);
    const [profilePic, setProfilePic] = useState(initialSubscriber?.profile_pic ?? "");
    const [subInfo, setSubInfo] = useState(initialSubscriber?.data ?? []);
    const [video, setVideo] = useState("");
    const [banner, setBanner] = useState(initialSubscriber?.data?.banner_url || 'img/banners/' + domainSplit[1] + '.' + subdomain + ".jpg");

    useEffect(() => {
        // Seeded server-side — skip the redundant refetch on first mount (matches other pages).
        // Still runs when unseeded, e.g. client-side navigation into this page.
        if (initialSubscriber) return;

        fetch(withSite(API_URL + "/subscriber/getinfo?domain_name=" + subdomain))
            .then(response => response.json()).then(data => {
            if (data.code === 200) {
                setVideos(data?.data?.videos);

                // Runs only when server seeding failed, after mount, so a
                // random pick is safe here.
                setPlaylists(pickRandom(data?.data?.playlists, 6));
                setProfilePic(data?.data?.profile_pic);
                setSubInfo(data?.data?.data);

                if (data?.data?.data?.banner_url) {
                    setBanner(data.data.data.banner_url);
                }

                // If subInfo.enabled is not yes, redirect to main domain
                if (data?.data?.data?.enabled !== "yes") {
                    window.location.href = mainUrl;
                }
            } else if (data.code === 404) {
                window.location.href = mainUrl;
            }
        });
    }, [])

    return (<>
        {video && (<div className={"fixed z-10 right-0 bottom-0 md:right-4 md:bottom-3 rounded-lg bg-black"}
                        style={{maxWidth: "100%"}}>

            <button type="button" aria-label="Close video player" onClick={() => setVideo("")}
                    className="absolute z-10 right-0 p-2 text-white bg-black cursor-pointer rounded-b-lg w-10 h-10">
                <XMarkIcon className="w-6 h-6" aria-hidden="true"/>
            </button>

            <ReactPlayer
                width={400}
                height={225}
                style={{maxWidth: "100%"}}
                iframeClassName={"rounded-lg w-full"}
                url={`https://www.youtube-nocookie.com/watch?v=${video}`}
                playing
                config={{
                    youtube: {
                        height: "100%", host: "https://www.youtube-nocookie.com", playerVars: {
                            controls: 1,
                            autoplay: 1,
                            origin: typeof window !== 'undefined' ? window.location.origin : undefined,
                            modestbranding: 1,
                            fs: 0,
                            playsinline: 1,
                            iv_load_policy: 3,
                            rel: 0,
                        }
                    }
                }}
                onEnded={() => setVideo("")}
            />
        </div>)}

        <section
            style={"background-image: url(" + banner + ");box-shadow: inset 0 0 0 2000px rgba(0,0,0,.7);"}
            className="banner relative bg-no-repeat bg-cover bg-center bg-local md:bg-fixed">

            <Helmet>
                <title>{subInfo.name + " | " + messages["SITE.NAME"]}</title>
                <meta name="description" content={subInfo.subtitle}/>
                <meta property="og:image" content={profilePic}/>
            </Helmet>

            <div className="text-center lg:px-12 h-(--h-75v) flex flex-col justify-center items-center">
                <h1 className="mb-4 text-4xl font-extrabold md:text-5xl lg:text-6xl text-white">{subInfo.name}</h1>
                <p className="mb-8 text-lg font-normal lg:text-xl sm:px-16 xl:px-48 text-gray-400">{subInfo.subtitle}</p>
                <div
                    className="flex flex-col space-y-4 sm:flex-row sm:justify-center sm:space-y-0 sm:space-x-4">
                    <a href="#videos"
                       className="inline-flex justify-center items-center py-3 px-5 text-base font-medium text-center text-white rounded-lg border border-gray-300 hover:bg-white hover:text-black cursor-pointer">
                        <ArrowDownIcon className="mr-2 -ml-1 w-4 h-4"/>
                        Read more
                    </a>
                </div>
            </div>
        </section>

        <section id="videos" className="text-center bg-neutral-900">
            <div>
                <h2 className="uppercase font-bold text-white mb-4">New songs</h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5 mb-8">
                    {Object.keys(videos).map((key, index) => {
                        if (index > 7) {
                            return;
                        }
                        return (<a key={index} href="#" className="relative"
                                   onClick={(e) => {
                                       e.preventDefault();
                                       setVideo(key)
                                   }}>
                            <img className="w-full rounded-lg"
                                 src={`https://i1.ytimg.com/vi_webp/${key}/mqdefault.webp`} alt=""/>
                        </a>)
                    })}
                </div>
            </div>
        </section>

        <section id="support" className="text-center bg-black">
            <div>

                <h2 className="mb-2">Support me</h2>

                <p class="align-center">{subInfo.social_text}</p>
                <div className="flex justify-center mb-4">
                    <a href={subInfo.youtube} target="_blank" rel="noopener noreferrer"
                       className='flex items-center gap-4 bg-neutral-900 py-4 px-5 rounded-xl'>
                        <img src={profilePic} className="w-16 h-16"
                             alt={`${subInfo.name} profile picture`}/>
                        <div className="flex flex-col items-start">
                            <div className="text-white">{subInfo.name}</div>
                            <div
                                className="bg-red-400 text-white text-xs font-medium px-2.5 py-0.5 rounded-sm">Subscribe
                            </div>
                        </div>
                    </a>
                </div>

                <div className="flex justify-center gap-4">
                    {subInfo.youtube && (<a href={subInfo.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                        <svg id="youtube" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width="25"
                             height="25" fill="#fff">
                            <path
                                d="M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305zm-317.51 213.508V175.185l142.739 81.205-142.739 81.201z"/>
                        </svg>
                    </a>)}
                    {subInfo.facebook && (<a href={subInfo.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                        <svg id="facebook" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="25"
                             height="25" fill="#fff">
                            <path
                                d="M504 256C504 119 393 8 256 8S8 119 8 256c0 123.78 90.69 226.38 209.25 245V327.69h-63V256h63v-54.64c0-62.15 37-96.48 93.67-96.48 27.14 0 55.52 4.84 55.52 4.84v61h-31.28c-30.8 0-40.41 19.12-40.41 38.73V256h68.78l-11 71.69h-57.78V501C413.31 482.38 504 379.78 504 256z"/>
                        </svg>
                    </a>)}
                    {subInfo.twitter && (<a href={subInfo.twitter} target="_blank" rel="noopener noreferrer" aria-label="X">
                        <svg id="twitter" fill="#fff" xmlns="http://www.w3.org/2000/svg" width="25" height="25"
                             viewBox="0 0 512 512">
                            <path
                                d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z"/>
                        </svg>
                    </a>)}
                    {subInfo.instagram && (<a href={subInfo.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                        <svg id="instagram" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="25"
                             height="25" fill="#fff">
                            <path
                                d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/>
                        </svg>
                    </a>)}
                    {subInfo.discord && (<a href={subInfo.discord} target="_blank" rel="noopener noreferrer" aria-label="Discord">
                        <svg id="discord" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" width="25"
                             height="25" fill="#fff">
                            <path
                                d="M524.531,69.836a1.5,1.5,0,0,0-.764-.7A485.065,485.065,0,0,0,404.081,32.03a1.816,1.816,0,0,0-1.923.91,337.461,337.461,0,0,0-14.9,30.6,447.848,447.848,0,0,0-134.426,0,309.541,309.541,0,0,0-15.135-30.6,1.89,1.89,0,0,0-1.924-.91A483.689,483.689,0,0,0,116.085,69.137a1.712,1.712,0,0,0-.788.676C39.068,183.651,18.186,294.69,28.43,404.354a2.016,2.016,0,0,0,.765,1.375A487.666,487.666,0,0,0,176.02,479.918a1.9,1.9,0,0,0,2.063-.676A348.2,348.2,0,0,0,208.12,430.4a1.86,1.86,0,0,0-1.019-2.588,321.173,321.173,0,0,1-45.868-21.853,1.885,1.885,0,0,1-.185-3.126c3.082-2.309,6.166-4.711,9.109-7.137a1.819,1.819,0,0,1,1.9-.256c96.229,43.917,200.41,43.917,295.5,0a1.812,1.812,0,0,1,1.924.233c2.944,2.426,6.027,4.851,9.132,7.16a1.884,1.884,0,0,1-.162,3.126,301.407,301.407,0,0,1-45.89,21.83,1.875,1.875,0,0,0-1,2.611,391.055,391.055,0,0,0,30.014,48.815,1.864,1.864,0,0,0,2.063.7A486.048,486.048,0,0,0,610.7,405.729a1.882,1.882,0,0,0,.765-1.352C623.729,277.594,590.933,167.465,524.531,69.836ZM222.491,337.58c-28.972,0-52.844-26.587-52.844-59.239S193.056,219.1,222.491,219.1c29.665,0,53.306,26.82,52.843,59.239C275.334,310.993,251.924,337.58,222.491,337.58Zm195.38,0c-28.971,0-52.843-26.587-52.843-59.239S388.437,219.1,417.871,219.1c29.667,0,53.307,26.82,52.844,59.239C470.715,310.993,447.538,337.58,417.871,337.58Z"/>
                        </svg>
                    </a>)}
                    {subInfo.tiktok && (<a href={subInfo.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                        <svg id="tiktok" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="25"
                             height="25"
                             fill="#fff">
                            <path
                                d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"/>
                        </svg>
                    </a>)}
                    {subInfo.soundcloud && (<a href={subInfo.soundcloud} target="_blank" rel="noopener noreferrer" aria-label="SoundCloud">
                        <svg id="soundcloud" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" width="25"
                             height="25" fill="#fff">
                            <path
                                d="M111.4 256.3l5.8 65-5.8 68.3c-.3 2.5-2.2 4.4-4.4 4.4s-4.2-1.9-4.2-4.4l-5.6-68.3 5.6-65c0-2.2 1.9-4.2 4.2-4.2 2.2 0 4.1 2 4.4 4.2zm21.4-45.6c-2.8 0-4.7 2.2-5 5l-5 105.6 5 68.3c.3 2.8 2.2 5 5 5 2.5 0 4.7-2.2 4.7-5l5.8-68.3-5.8-105.6c0-2.8-2.2-5-4.7-5zm25.5-24.1c-3.1 0-5.3 2.2-5.6 5.3l-4.4 130 4.4 67.8c.3 3.1 2.5 5.3 5.6 5.3 2.8 0 5.3-2.2 5.3-5.3l5.3-67.8-5.3-130c0-3.1-2.5-5.3-5.3-5.3zM7.2 283.2c-1.4 0-2.2 1.1-2.5 2.5L0 321.3l4.7 35c.3 1.4 1.1 2.5 2.5 2.5s2.2-1.1 2.5-2.5l5.6-35-5.6-35.6c-.3-1.4-1.1-2.5-2.5-2.5zm23.6-21.9c-1.4 0-2.5 1.1-2.5 2.5l-6.4 57.5 6.4 56.1c0 1.7 1.1 2.8 2.5 2.8s2.5-1.1 2.8-2.5l7.2-56.4-7.2-57.5c-.3-1.4-1.4-2.5-2.8-2.5zm25.3-11.4c-1.7 0-3.1 1.4-3.3 3.3L47 321.3l5.8 65.8c.3 1.7 1.7 3.1 3.3 3.1 1.7 0 3.1-1.4 3.1-3.1l6.9-65.8-6.9-68.1c0-1.9-1.4-3.3-3.1-3.3zm25.3-2.2c-1.9 0-3.6 1.4-3.6 3.6l-5.8 70 5.8 67.8c0 2.2 1.7 3.6 3.6 3.6s3.6-1.4 3.9-3.6l6.4-67.8-6.4-70c-.3-2.2-2-3.6-3.9-3.6zm241.4-110.9c-1.1-.8-2.8-1.4-4.2-1.4-2.2 0-4.2.8-5.6 1.9-1.9 1.7-3.1 4.2-3.3 6.7v.8l-3.3 176.7 1.7 32.5 1.7 31.7c.3 4.7 4.2 8.6 8.9 8.6s8.6-3.9 8.6-8.6l3.9-64.2-3.9-177.5c-.4-3-2-5.8-4.5-7.2zm-26.7 15.3c-1.4-.8-2.8-1.4-4.4-1.4s-3.1.6-4.4 1.4c-2.2 1.4-3.6 3.9-3.6 6.7l-.3 1.7-2.8 160.8s0 .3 3.1 65.6v.3c0 1.7.6 3.3 1.7 4.7 1.7 1.9 3.9 3.1 6.4 3.1 2.2 0 4.2-1.1 5.6-2.5 1.7-1.4 2.5-3.3 2.5-5.6l.3-6.7 3.1-58.6-3.3-162.8c-.3-2.8-1.7-5.3-3.9-6.7zm-111.4 22.5c-3.1 0-5.8 2.8-5.8 6.1l-4.4 140.6 4.4 67.2c.3 3.3 2.8 5.8 5.8 5.8 3.3 0 5.8-2.5 6.1-5.8l5-67.2-5-140.6c-.2-3.3-2.7-6.1-6.1-6.1zm376.7 62.8c-10.8 0-21.1 2.2-30.6 6.1-6.4-70.8-65.8-126.4-138.3-126.4-17.8 0-35 3.3-50.3 9.4-6.1 2.2-7.8 4.4-7.8 9.2v249.7c0 5 3.9 8.6 8.6 9.2h218.3c43.3 0 78.6-35 78.6-78.3.1-43.6-35.2-78.9-78.5-78.9zm-296.7-60.3c-4.2 0-7.5 3.3-7.8 7.8l-3.3 136.7 3.3 65.6c.3 4.2 3.6 7.5 7.8 7.5 4.2 0 7.5-3.3 7.5-7.5l3.9-65.6-3.9-136.7c-.3-4.5-3.3-7.8-7.5-7.8zm-53.6-7.8c-3.3 0-6.4 3.1-6.4 6.7l-3.9 145.3 3.9 66.9c.3 3.6 3.1 6.4 6.4 6.4 3.6 0 6.4-2.8 6.7-6.4l4.4-66.9-4.4-145.3c-.3-3.6-3.1-6.7-6.7-6.7zm26.7 3.4c-3.9 0-6.9 3.1-6.9 6.9L227 321.3l3.9 66.4c.3 3.9 3.1 6.9 6.9 6.9s6.9-3.1 6.9-6.9l4.2-66.4-4.2-141.7c0-3.9-3-6.9-6.9-6.9z"/>
                        </svg>
                    </a>)}
                    {subInfo.patreon && (<a href={subInfo.patreon} target="_blank" rel="noopener noreferrer" aria-label="Patreon">
                        <svg id="patreon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="25"
                             height="25" fill="#fff">
                            <path
                                d="M512 194.8c0 101.3-82.4 183.8-183.8 183.8-101.7 0-184.4-82.4-184.4-183.8 0-101.6 82.7-184.3 184.4-184.3C429.6 10.5 512 93.2 512 194.8zM0 501.5h90v-491H0v491z"/>
                        </svg>
                    </a>)}
                    {subInfo.rumble && (<a href={subInfo.rumble} target="_blank" rel="noopener noreferrer" aria-label="Rumble">
                        <svg id="rumble" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1441 1583" width="25"
                             height="25">
                            <path id="Layer" fill-rule="evenodd" fill="#fff"
                                  d="m1339.3 537.2c32.1 35.2 57.5 75.9 74.8 120.3 17.2 44.4 26.1 91.5 26.2 139.2 0.1 47.6-8.6 94.8-25.7 139.2-17.1 44.4-42.3 85.3-74.3 120.6-57.2 63.1-119 121.9-184.9 175.9-65.9 54-135.7 103-208.9 146.7-73.1 43.6-149.4 81.8-228.2 114.2-78.8 32.4-159.9 59-242.6 79.4-42.2 10.6-86.1 12.9-129.1 6.8-43.1-6.1-84.6-20.4-122.2-42.3-37.7-21.8-70.7-50.7-97.4-85-26.6-34.4-46.4-73.6-58.2-115.4-100.4-343-85.6-730.8 11.2-1075.3 51-180.9 221.3-294.5 396.7-252.7 324.8 77.4 629.6 276.7 862.6 528.4zm-457.3 356.2c61.2-48.4 61.2-142.7 0-192.6q-32.6-27.1-66.8-52.1-34.2-25.1-69.9-48-35.6-22.9-72.6-43.7-37-20.7-75.2-39.2c-70.3-33.6-148.8 13.3-160.1 93.3-17.3 122.3-20.4 245.6-9.1 362.8 7.6 81.1 85.1 129.5 156.5 98.4q39.9-17.2 78.6-37 38.7-19.8 76-42.2 37.3-22.4 73-47.2 35.7-24.8 69.6-52z"/>
                        </svg>
                    </a>)}
                    {subInfo.spotify && (<a href={subInfo.spotify} target="_blank" rel="noopener noreferrer" aria-label="Spotify">
                        <svg id="spotify" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 496 512" width="23"
                             height="23">
                            <path fill="#fff"
                                  d="M248 8C111.1 8 0 119.1 0 256s111.1 248 248 248 248-111.1 248-248S384.9 8 248 8zm100.7 364.9c-4.2 0-6.8-1.3-10.7-3.6-62.4-37.6-135-39.2-206.7-24.5-3.9 1-9 2.6-11.9 2.6-9.7 0-15.8-7.7-15.8-15.8 0-10.3 6.1-15.2 13.6-16.8 81.9-18.1 165.6-16.5 237 26.2 6.1 3.9 9.7 7.4 9.7 16.5s-7.1 15.4-15.2 15.4zm26.9-65.6c-5.2 0-8.7-2.3-12.3-4.2-62.5-37-155.7-51.9-238.6-29.4-4.8 1.3-7.4 2.6-11.9 2.6-10.7 0-19.4-8.7-19.4-19.4s5.2-17.8 15.5-20.7c27.8-7.8 56.2-13.6 97.8-13.6 64.9 0 127.6 16.1 177 45.5 8.1 4.8 11.3 11 11.3 19.7-.1 10.8-8.5 19.5-19.4 19.5zm31-76.2c-5.2 0-8.4-1.3-12.9-3.9-71.2-42.5-198.5-52.7-280.9-29.7-3.6 1-8.1 2.6-12.9 2.6-13.2 0-23.3-10.3-23.3-23.6 0-13.6 8.4-21.3 17.4-23.9 35.2-10.3 74.6-15.2 117.5-15.2 73 0 149.5 15.2 205.4 47.8 7.8 4.5 12.9 10.7 12.9 22.6 0 13.6-11 23.3-23.2 23.3z"/>
                        </svg>
                    </a>)}
                </div>
            </div>
        </section>


        <section id="more" className="text-left bg-neutral-900">
            <div>
                <h2>More {subInfo.name}</h2>
                <div className="flex flex-col-reverse md:flex-row gap-5">
                    {playlists.length > 0 && (
                        <div
                            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-5 bg-black rounded-lg p-5">
                            {Object.keys(playlists).map((key, index) => {
                                return (
                                    <a
                                        key={index}
                                        target="_blank" rel="noopener noreferrer"
                                        href={playlists[key]['url']}
                                        className="group relative block aspect-video overflow-hidden rounded-lg"
                                    >
                                        <img
                                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                            src={playlists[key]['thumbnail']}
                                            alt={playlists[key]['title']}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent"/>
                                        <span
                                            className="absolute bottom-0 left-0 right-0 p-2 text-white font-bold uppercase text-sm leading-tight line-clamp-2 [text-shadow:_0_1px_3px_rgb(0_0_0_/_80%)]">
                                            {playlists[key]['title']}
                                        </span>
                                    </a>
                                )

                            })}
                        </div>
                    )}
                    <div className="flex flex-col justify-between max-w-full md:min-w-100 bg-black rounded-lg p-5">
                        {subInfo.id && (<RadioComponent subscribers_id={subInfo.id} compactUpcoming/>)}
                    </div>
                </div>
            </div>
        </section>

        <section id="about" className="text-center bg-black">
            <div>
                <h2>About me</h2>
                <div className="flex flex-col md:flex-row gap-10 justify-between">
                    <p className="text-justify whitespace-break-spaces">{linkify(subInfo.about_me)}</p>
                    <img src={profilePic} className="w-64" style={{height: "16rem"}}
                         alt={`${subInfo.name} profile picture`}/>
                </div>
            </div>
        </section>

        <section className="text-center bg-neutral-900">
            <div className="text-white text-center">
                Powered by
                <a href={mainUrl} target="_blank" rel="noopener noreferrer"><img className="mx-auto my-4 w-80"
                                                       alt={`${messages["SITE.NAME"]} website logo`}
                                                       src={"/img/" + messages["LOGO"]}/></a>
                © Copyright {messages["SITE.NAME"]} {year}.
            </div>
        </section>
    </>);
}
